import createDOMPurify from 'dompurify';
import { marked } from 'marked';

export type MarkdownPreviewSegment =
  | { type: 'html'; html: string }
  | { type: 'mermaid'; id: string; source: string };

const MERMAID_FENCE_RE = /^```mermaid[ \t]*\r?\n([\s\S]*?)```/gim;
const FORBIDDEN_TAGS = new Set(['script', 'style', 'iframe', 'object', 'embed', 'form']);

marked.setOptions({
  gfm: true,
  breaks: false
});

type PurifyLike = {
  sanitize: (html: string, config?: Record<string, unknown>) => string;
};

function getDOMPurify(): PurifyLike | null {
  if (typeof window === 'undefined') return null;
  const mod = createDOMPurify as unknown as PurifyLike & ((win: Window) => PurifyLike);
  if (typeof mod?.sanitize === 'function') return mod;
  if (typeof mod === 'function') {
    try {
      return mod(window);
    } catch {
      return null;
    }
  }
  return null;
}

/** Fallback sanitizer for environments where DOMPurify cannot use a real DOM (e.g. happy-dom). */
function sanitizeWithDomParser(html: string): string {
  if (typeof document === 'undefined') return html;
  const template = document.createElement('template');
  template.innerHTML = html;
  template.content.querySelectorAll([...FORBIDDEN_TAGS].join(',')).forEach((el) => el.remove());
  template.content.querySelectorAll('*').forEach((el) => {
    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase();
      if (name === 'style' || name.startsWith('on') || name === 'srcdoc') {
        el.removeAttribute(attr.name);
        continue;
      }
      if ((name === 'href' || name === 'src' || name === 'xlink:href') && /^\s*javascript:/i.test(attr.value)) {
        el.removeAttribute(attr.name);
      }
    }
  });
  return template.innerHTML;
}

function sanitizeHtml(html: string): string {
  const purify = getDOMPurify();
  if (purify) {
    const probe = purify.sanitize('<p>t</p>', { USE_PROFILES: { html: true } });
    if (probe.includes('<p>')) {
      return purify.sanitize(html, {
        FORBID_TAGS: [...FORBIDDEN_TAGS],
        FORBID_ATTR: ['style'],
        USE_PROFILES: { html: true }
      });
    }
  }
  return sanitizeWithDomParser(html);
}

/** Resolves mermaid theme from the active platform `html[data-theme]`. */
export function resolveMermaidTheme(): 'dark' | 'default' {
  if (typeof document === 'undefined') return 'default';
  return document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'default';
}

/**
 * Splits markdown into sanitized HTML segments and Mermaid source blocks.
 * Mermaid fences are extracted before marked so they are never double-escaped.
 */
export function buildMarkdownPreviewSegments(markdown: string): MarkdownPreviewSegment[] {
  const source = markdown ?? '';
  if (!source.trim()) {
    return [{ type: 'html', html: '' }];
  }

  const segments: MarkdownPreviewSegment[] = [];
  let lastIndex = 0;
  let mermaidIndex = 0;
  const re = new RegExp(MERMAID_FENCE_RE.source, MERMAID_FENCE_RE.flags);
  let match: RegExpExecArray | null;

  while ((match = re.exec(source)) !== null) {
    const before = source.slice(lastIndex, match.index);
    if (before) {
      const html = sanitizeHtml(String(marked.parse(before, { async: false })));
      if (html.trim()) segments.push({ type: 'html', html });
    }
    segments.push({
      type: 'mermaid',
      id: `mermaid-${mermaidIndex++}`,
      source: (match[1] ?? '').trimEnd()
    });
    lastIndex = match.index + match[0].length;
  }

  const rest = source.slice(lastIndex);
  if (rest || segments.length === 0) {
    const html = sanitizeHtml(String(marked.parse(rest || source, { async: false })));
    segments.push({ type: 'html', html });
  }

  return segments;
}

/** Short title for the Mermaid popup from the first non-empty diagram line. */
export function mermaidDiagramTitle(source: string): string {
  const line = source
    .split(/\r?\n/)
    .map((value) => value.trim())
    .find((value) => value.length > 0 && !value.startsWith('%%'));
  if (!line) return 'Mermaid diagram';
  return line.length > 48 ? `${line.slice(0, 45)}…` : line;
}
